import React, { useState, useEffect, useCallback } from "react";
import "./referral.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faGift, faCircleCheck, faHourglassHalf, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

const statusLabels = {
  pending: { text: "Pending email verification", icon: faHourglassHalf, cls: "pending" },
  qualified: { text: "Verified - counts toward reward", icon: faCircleCheck, cls: "qualified" },
  rewarded: { text: "Rewarded 🎉", icon: faGift, cls: "rewarded" },
  flagged: { text: "Under review", icon: faTriangleExclamation, cls: "flagged" },
  rejected: { text: "Rejected", icon: faTriangleExclamation, cls: "rejected" },
};

const Referral = ({ isLoggedIn, loginUser, onLogout }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleError = useCallback((err) => {
    if (err.response && err.response.status === 401) {
      onLogout();
      navigate("/login");
    } else if (err.response) {
      setError(err.response.data.message || "⚠️ Something went wrong.");
    } else {
      setError("⚠️ An error occurred. Please try again.");
    }
  }, [onLogout, navigate]);

  useEffect(() => {
    const getSummary = async () => {
      try {
        const response = await axios.get("/api/v1/referral/me");
        setSummary(response.data);
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };
    getSummary();
  }, [handleError]);

  const handleCreateCode = async () => {
    try {
      setCreating(true);
      setError("");
      const response = await axios.post("/api/v1/referral/code");
      setSummary((prev) => ({ ...prev, referralCode: response.data.referralCode }));
    } catch (err) {
      handleError(err);
    } finally {
      setCreating(false);
    }
  };

  const shareLink = summary && summary.referralCode
    ? `${window.location.origin}/login?ref=${summary.referralCode}`
    : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError("⚠️ Could not copy. Please copy the link manually.");
    }
  };

  if (!isLoggedIn) {
    navigate("/");
    return null;
  }

  if (loading) {
    return (
      <div className="referral">
        <div className="referral__top">
          <div className="referral__top-content">
            <h1 className="referral__title">Refer &amp; Earn</h1>
          </div>
        </div>
        <div className="referral__body">
          <p className="referral__hint">Loading your referrals...</p>
        </div>
      </div>
    );
  }

  const threshold = summary ? summary.threshold : 5;
  const rewardAmount = summary ? summary.rewardAmount : 1000;
  const towardNext = summary ? summary.towardNext : 0;
  const progressPct = Math.min(100, Math.round((towardNext / threshold) * 100));

  return (
    <div className="referral">
      <div className="referral__top">
        <div className="referral__top-content">
          <h1 className="referral__title">Refer &amp; Earn</h1>
          <p className="referral__subtitle">Invite friends to SastiRide. For every <strong>{threshold}</strong> friends who sign up and
            verify their account with your code, you earn <strong>Rs. {rewardAmount}</strong>.</p>
        </div>
      </div>

      <div className="referral__body">

      {error !== "" && <div className="referral__error">{error}</div>}

      {/* Code / share section */}
      <div className="referral__card">
        {summary && summary.referralCode ? (
          <>
            <h3>Your referral code</h3>
            <div className="referral__code">{summary.referralCode}</div>
            <div className="referral__share">
              <input type="text" readOnly value={shareLink} onFocus={(e) => e.target.select()} />
              <button onClick={handleCopy}>
                <FontAwesomeIcon icon={faCopy} /> {copied ? "Copied!" : "Copy link"}
              </button>
            </div>
            <p className="referral__hint">Share this link. Friends who sign up through it are tracked automatically.</p>
          </>
        ) : (
          <>
            <h3>Create your referral code</h3>
            <p className="referral__hint">You get one unique code to share. Generate it once and start inviting.</p>
            <button className="referral__create-btn" onClick={handleCreateCode} disabled={creating}>
              <FontAwesomeIcon icon={faGift} /> {creating ? "Creating..." : "Create my code"}
            </button>
          </>
        )}
      </div>

      {/* Progress + stats */}
      {summary && summary.referralCode && (
        <>
          <div className="referral__card">
            <h3>Progress to your next Rs. {rewardAmount}</h3>
            <div className="referral__progress">
              <div className="referral__progress-bar" style={{ width: `${progressPct}%` }} />
            </div>
            <p className="referral__progress-text">{towardNext} / {threshold} verified signups</p>
          </div>

          <div className="referral__stats">
            <div className="referral__stat">
              <span className="referral__stat-num">{summary.rewardsEarned}</span>
              <span className="referral__stat-label">Rewards earned</span>
            </div>
            <div className="referral__stat">
              <span className="referral__stat-num">Rs. {summary.rewardBalance}</span>
              <span className="referral__stat-label">Unpaid balance</span>
            </div>
            <div className="referral__stat">
              <span className="referral__stat-num">Rs. {summary.rewardPaid}</span>
              <span className="referral__stat-label">Paid out</span>
            </div>
            <div className="referral__stat">
              <span className="referral__stat-num">{summary.successful}</span>
              <span className="referral__stat-label">Verified signups</span>
            </div>
          </div>

          {/* History */}
          <div className="referral__card">
            <h3>Your invites ({summary.counts.total})</h3>
            {summary.history.length === 0 ? (
              <p className="referral__hint">No invites yet. Share your link to get started!</p>
            ) : (
              <ul className="referral__history">
                {summary.history.map((item) => {
                  const label = statusLabels[item.status] || statusLabels.pending;
                  return (
                    <li key={item._id} className={`referral__history-item ${label.cls}`}>
                      <span className="referral__history-name">{item.userName}</span>
                      <span className="referral__history-status">
                        <FontAwesomeIcon icon={label.icon} /> {label.text}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default Referral;
